import { Component, OnInit, ViewChild } from "@angular/core";
import { MatSort, MatSortable, Sort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { ERTUser } from "../interfaces/ert-user/ert-user";
import { Router } from "@angular/router";
import { MatPaginator } from "@angular/material/paginator";
import { ERTUserService } from "../services/ert-user/ert-user.service";
import { FormBuilder, FormGroup } from "@angular/forms";
import {
  FilterCriteria,
  FilterService,
} from "../services/common/filter.service";
import { LoggedUserService } from "../services/common/logged-user.service";
import { ILoggedUserData } from "../interfaces/common/loggedUserData";

@Component({
  selector: "ert-user-component",
  templateUrl: "./ert-user.component.html",
  styles: [
    `
      .button-container {
        display: flex;
        justify-content: space-between;
        margin-bottom: 10px;
      }
    `,
  ],
})
export class ERTUserComponent implements OnInit {
  title = "User";
  totalItems: number;
  sortedBy: string;
  menuTriggerData: any = {};
  searchForm: FormGroup;

  displayedColumns: Map<string, string> = new Map<string, string>([
    ["id", "User Number"],
    ["isInstructor", "Instructor"],
    ["isCompletedFCE", "FCE"],
    ["fullName", "Full Name"],
    ["lastName", "Last Name"],
    ["firstName", "First Name"],
    ["caiEmail", "Email"],
    ["crew", "Crew"],
    ["unit", "Unit"],
    ["division", "Division"],
    ["options", "Options"],
  ]);
  dataSource: MatTableDataSource<ERTUser, MatPaginator> =
    new MatTableDataSource<ERTUser, MatPaginator>([]);
  displayedColumnsArray: string[] = Array.from(this.displayedColumns.keys());
  pageSize = 10;
  isUsersLoading = false;

  currentUser: ILoggedUserData;
  // Filter properties
  activeFilters: FilterCriteria[] = [];
  showFilterPanel = false;

  // Storage keys - use unique keys for this component
  readonly ert_user_filters_storage_key = "ert-user-filters";
  readonly ert_user_saved_filters_storage_key = "ert-user-saved-filters";

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  constructor(
    private router: Router,
    private userService: ERTUserService,
    private formBuilder: FormBuilder,
    private filterService: FilterService,
    private loggedUserService: LoggedUserService,
  ) {}

  ngOnInit() {
    this.initSearchForm();
    this.loggedUserService.getLoggedUserData().subscribe((user) => {
      this.currentUser = user;
      this.getAllUsers();
    });

    // Initialize filter service
    this.filterService.loadActiveFiltersFromStorage(
      this.ert_user_filters_storage_key
    );
    this.filterService.loadSavedFiltersFromStorage(
      this.ert_user_saved_filters_storage_key
    );
  }

  initSearchForm(): void {
    this.searchForm = this.formBuilder.group({
      searchTerm: [""],
    });

    this.searchForm.get("searchTerm")?.valueChanges.subscribe((value) => {
      this.applyFilter(value);
    });
  }

  clearSearch(): void {
    this.searchForm.get("searchTerm")?.setValue("");
  }

  applyFilter(filterValue: string): void {
    // Create custom filter predicate that combines search term with advanced filters
    this.dataSource.filterPredicate = (data: ERTUser, filterString: string) => {
      // Parse the filter string - format is "searchTerm||activeFilters"
      const parts = filterString.split("||");
      const searchTerm = parts[0].toLowerCase();
      let activeFilters: FilterCriteria[] = [];

      try {
        if (parts[1]) {
          activeFilters = JSON.parse(parts[1]);
        }
      } catch (error) {
        console.error("Error parsing active filters:", error);
      }

      // Basic search in name, email, CAI, etc.
      const searchMatch = searchTerm
        ? (data.fullName?.toLowerCase() || "").includes(searchTerm) ||
          (data.firstName?.toLowerCase() || "").includes(searchTerm) ||
          (data.lastName?.toLowerCase() || "").includes(searchTerm) ||
          (data.caiEmail?.toLowerCase() || "").includes(searchTerm) ||
          (data.crew?.toLowerCase() || "").includes(searchTerm) ||
          (data.unit?.toLowerCase() || "").includes(searchTerm) ||
          (data.division?.toLowerCase() || "").includes(searchTerm)
        : true; // If no search term, consider it a match

      // Advanced filters match
      let advancedFiltersMatch = true;
      if (activeFilters.length > 0) {
        advancedFiltersMatch = activeFilters.every((filter) => {
          const value = this.getPropertyValue(data, filter.column);

          if (value === null || value === undefined) {
            return false;
          }

          switch (filter.operator) {
            case "contains":
              return String(value)
                .toLowerCase()
                .includes(String(filter.value).toLowerCase());
            case "equals":
              return (
                String(value).toLowerCase() ===
                String(filter.value).toLowerCase()
              );
            case "startsWith":
              return String(value)
                .toLowerCase()
                .startsWith(String(filter.value).toLowerCase());
            case "endsWith":
              return String(value)
                .toLowerCase()
                .endsWith(String(filter.value).toLowerCase());
            case "greaterThan":
              return Number(value) > Number(filter.value);
            case "lessThan":
              return Number(value) < Number(filter.value);
            default:
              return true;
          }
        });
      }

      return searchMatch && advancedFiltersMatch;
    };

    // Combine search term and active filters
    const combinedFilter =
      filterValue.trim().toLowerCase() +
      "||" +
      JSON.stringify(this.activeFilters);
    this.dataSource.filter = combinedFilter;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }

    this.totalItems = this.dataSource.filteredData.length;
  }

  getPropertyValue(data: ERTUser, property: string): any {
    switch (property) {
      case "id":
        return data.id;
      case "fullName":
        return data.fullName;
      case "firstName":
        return data.firstName;
      case "lastName":
        return data.lastName;
      case "caiEmail":
        return data.caiEmail;
      case "crew":
        return data.crew;
      case "unit":
        return data.unit;
      case "division":
        return data.division;
      case "isInstructor":
        return data.isInstructor ? "Yes" : "No";
      case "isCompletedFCE":
        return data.isCompletedFCE ? "Yes" : "No";
      default:
        return "";
    }
  }

  toggleFilterPanel() {
    this.showFilterPanel = !this.showFilterPanel;
  }

  onFiltersChanged(filters: FilterCriteria[]): void {
    this.applyFilter(this.searchForm.get("searchTerm")?.value || "");
  }

  clearAllFilters() {
    this.filterService.clearAllFilters(this.ert_user_filters_storage_key);
    this.searchForm.patchValue({ searchTerm: "" });
    this.applyFilter("");
  }

  getColumnDisplayName(columnKey: string): string {
    return this.displayedColumns.get(columnKey) || columnKey;
  }

  getAllUsers() {
    this.isUsersLoading = true;
    const facilityId = Number(this.currentUser?.facilityId) || 1;
    this.userService.getAllERTUsers(facilityId).subscribe({
      next: (data) => {
        this.dataSource = new MatTableDataSource<ERTUser, MatPaginator>(data);
        this.totalItems = data.length;
        setTimeout(() => {
          this.dataSource.sort = this.sort;
          this.dataSource.paginator = this.paginator;
          this.sort.sort({ id: "id", start: "asc" } as MatSortable);
          this.sort.sortChange.subscribe((sortState: Sort) => {
            this.onSortChange(sortState);
          });
          this.sortedBy = `Sorted by ${this.displayedColumns.get("id")}`;
        }, 10);
        // Subscribe to active filters changes
        this.filterService
          .getActiveFilters(this.ert_user_filters_storage_key)
          .subscribe((filters) => {
            this.activeFilters = filters;
            if (this.dataSource) {
              this.applyFilter(this.searchForm.get("searchTerm")?.value || "");
            }
          });
        this.isUsersLoading = false;
      },
      error: (error) => {
        this.isUsersLoading = false;
        console.log(error);
      },
    });
  }

  onSortChange(sortState: Sort) {
    if (sortState.direction.length > 0) {
      this.sortedBy = `Sorted by ${this.displayedColumns.get(
        sortState.active
      )}`;
    } else {
      this.sortedBy = "Not sorted";
    }
  }

  editUser(user: any) {
    this.router.navigate(["/ert-user/ert-user-view"], {
      queryParams: { usernumber: user.id },
    });
  }

  deleteUser(userId: any) {
    if (window.confirm("Are you sure you want to delete this user?")) {
      this.userService.deleteERTUser(userId, Number(this.currentUser.facilityId) || 0).subscribe({
        next: () => {
          this.getAllUsers();
        },
        error: (error) => {
          console.log(error);
        },
      });
    }
  }

  onMenuOpen(data: any) {
    this.menuTriggerData = data;
  }
}
